import { Theme } from '@mui/material/styles';
import { radiusHalf } from 'utility/borderRadius';
import type { CSSProperties } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { baseTransition, getColorMix, getSuccessBgColor } from 'utility/styles';

const shortInputWidthPx = 242;
const formContentMaxWidthPx = 800;
export const metaMaxWidthPx = 600;
const selectHeight = 25;
const inputHeightPx = 30;
const inputFontSizePx = 16;
const inputMargin = '0.5em 0';
const textAreaInputHeight = 120;
export const marginOverscroll = 25;
export const articleMaxWidth = 1200;
export const articleIndentDnD = `max(min(200px, calc((100% - 800px) / 2)), 8px)`;
// Seems to be good maxWidth is 1200px for the form. Good area for drag handles to not disappear.
// Should max out at 200px on larger screens, and shrink down to 8px at a minimum.

export const getPrintTrimStyles = (theme: Theme): SystemStyleObject => ({
  backgroundColor: getColorMix(
    theme.blox.colors.violet,
    25,
    theme.palette.background.default,
    'srgb',
  ),
  outline: `1px solid ${theme.blox.colors.violet}`,
  color: getColorMix(theme.blox.colors.violet, 25, theme.palette.text.primary, 'srgb'),
  borderRadius: '4px',
  textDecoration: 'none',
  display: 'inline',
  alignItems: 'center',
  width: 'fit-content',
  padding: '1px 2px 1px 2px',
  margin: '1px',
  '&:before': {
    display: 'inline-block',
    verticalAlign: 'text-top',
    height: '18px',
    width: '18px',
    content: `url("data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' fill='${theme.blox.colors.violet}' viewBox='0 0 24 24'><path d='M19.1 17H22v-6c0-1.7-1.3-3-3-3h-9zm-.1-7c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1m-1-3V3H6v1.1L9 7zM1.2 1.8 0 3l4.9 5C3.3 8.1 2 9.4 2 11v6h4v4h11.9l3 3 1.3-1.3zM8 19v-5h2.9l5 5z'/></svg>`,
    )}")`,
  },
});

export const getPageBreakStyles = (theme: Theme): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  textAlign: 'center',
  position: 'relative',
  height: 18,
  '&:before': {
    content: '""',
    border: '1.5px dashed',
    borderColor: theme.palette.grey[500],
    position: 'absolute',
    left: 0,
    top: '50%',
    width: '100%',
  },
  '&:after': {
    content: '"Page Break"',
    position: 'absolute',
    whiteSpace: 'nowrap',
    fontWeight: '500',
    textAlign: 'center',
    border: `2px solid ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.grey[500],
    px: 1.5,
  },
});

type ArticleInputsWrapperSxArgs = {
  theme: Theme;
  showCoverArtArea?: boolean;
};

// Tiptap, Prosemirror, and other styles for Articles
export const articleInputsWrapperSx = ({
  theme,
  showCoverArtArea = false,
}: ArticleInputsWrapperSxArgs): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  pt: showCoverArtArea ? 0 : 2,
  pb: 38, // Pad the bottom of the edit view to prevent jump when menus open near the bottom of the editors
  pl: 0,
  pr: 0,
  width: '100%',
  maxWidth: articleMaxWidth,
  fontSize: '1rem',
  borderLeft: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  '& .content-authors': {
    px: articleIndentDnD,
    // override styles for authors component on article form
    '& .input-field.input-array .tiptap.ProseMirror': {
      p: 0,
      m: 0,
      outline: 'none',
      outlineOffset: 0,
      bgcolor: 'transparent',
      border: 'none',
    },
  },
  '& div[class*="content-hidden"]': {
    display: 'none',
    marginBottom: 0,
  },
  '& > *:empty': {
    display: 'none',
  },
  '& #related-default-top, & #related-default-bottom': {
    px: articleIndentDnD,
  },
  '& #related-default-bottom div[id^="DndLiveRegion-"]': {
    position: 'relative !important', // Fix for DndLiveRegion (Screen reader info div) causing overflow
  },
  '& .ProseMirror': {
    '& p': {
      my: '0.5rem', // spacing between paragraphs
    },
    /**
     * CMSADMIN-1001 - legacy embedded images
     * see ticket / useCreateEditor image
     * extension comments for more details.
     */
    '& img': {
      display: 'inline-block',
      maxWidth: '100%',
      height: 'auto',
      width: 'auto',
      borderRadius: '4px',
    },
    px: articleIndentDnD,
    textWrap: 'pretty',
    textRendering: 'optimizeLegibility',
    fontFeatureSettings: 'normal',
    borderBottom: '1px solid transparent',
    '&:focus, :hover': {
      outline: 'none',
    },
    '& hr.tncms-page-break': getPageBreakStyles(theme),
    '& ins.note': {
      backgroundColor: getSuccessBgColor(theme, false),
      outline: `1px solid ${theme.palette.success.dark}`,
      color: getColorMix(
        theme.palette.success.dark,
        25,
        theme.palette.text.primary,
        'srgb',
      ),
      borderRadius: '4px',
      textDecoration: 'none',
      display: 'inline',
      alignItems: 'center',
      width: 'fit-content',
      padding: '1px 2px 1px 2px',
      margin: '1px',
      '&:before': {
        display: 'inline-block',
        verticalAlign: 'text-top',
        height: '18px',
        width: '18px',
        content: `url("data:image/svg+xml,${encodeURIComponent(
          `<svg xmlns='http://www.w3.org/2000/svg' fill='${theme.palette.success.dark}' viewBox='0 0 24 24'><path d='M3 10h11v2H3v-2zm0-2h11V6H3v2zm0 8h7v-2H3v2zm15.01-3.13.71-.71c.39-.39 1.02-.39 1.41 0l.71.71c.39.39.39 1.02 0 1.41l-.71.71-2.12-2.12zm-.71.71-5.3 5.3V21h2.12l5.3-5.3-2.12-2.12z'/></svg>`,
        )}")`,
      },
    },
    '& span.print_trim': getPrintTrimStyles(theme),
  },

  // Targets spacing between editors and paragraphs
  '& .input-field.input-text .tiptap.ProseMirror, & .input-field.input-text .tiptap.ProseMirror p':
    {
      my: 0,
    },

  // Error notices under tiptap inputs
  '& .error-notice': {
    px: articleIndentDnD,
    mt: 0,
    py: 0,
    color: theme.palette.error.main,
    '& svg': {
      fontSize: theme.typography.fontSize,
    },
    '& .error-text': {
      fontSize: theme.typography.fontSize,
    },
  },
  '& .has-error > .ProseMirror > p': {
    borderBottom: `2px solid ${theme.palette.error.main}`,
  },
});

// Main Article Editor Bar
export const articleEditorBarSx = (theme: Theme) => ({
  width: '100%',
  minWidth: 'fit-content', // Fill area on overflow, will be fixed in CMSADMIN-168
  '&.MuiToolbar-root': {
    p: 0,
    m: 0,
    minHeight: 'auto',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
    '& .MuiToggleButtonGroup-root': {
      display: 'flex',
      justifyContent: 'center',
      maxWidth: articleMaxWidth,
      width: '100%',
    },
    '& button': {
      borderColor: 'transparent',
    },
    '& .MuiButton-root, & .MuiButtonBase-root': {
      color:
        theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[400],
      "&:not([class*='-disabled'])": {
        color:
          theme.palette.mode === 'dark'
            ? theme.palette.grey[200]
            : theme.palette.grey[800],
      },
    },
  },
});

// Left Drawer buttongrouped toggle style arrow
export const getDrawerToggleSx = (theme: Theme): SystemStyleObject => {
  return {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    width: '20px',
    border: 'none',
    minWidth: '25px',
    transition: `outline outline-offset backgroundColor ${baseTransition}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      outline: `2px solid ${theme.palette.action.hover}`,
      outlineOffset: '-2px',
    },
  };
};

// Metadata container
export const metadataBoxStyle = (
  theme: Theme,
  leftDrawerContent: string,
): SystemStyleObject => {
  return {
    display: leftDrawerContent === 'metadata' ? 'flex' : 'none',
    flexDirection: 'column',
    px: 1,
    maxWidth: metaMaxWidthPx,
    '& > *:last-child': { mb: marginOverscroll },
    // Separation dividers, pseudo-element is dividing line
    '& .meta-group': {
      pb: 2,
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: theme.spacing(-7), // offset padding
        bottom: 0,
        width: `calc(100% + ${theme.spacing(7)})`,
        height: '1px',
        backgroundColor: `${theme.palette.divider}`,
        backgroundImage: `linear-gradient(to right, ${theme.palette.divider} 75%, ${theme.palette.background.paper})`,
      },
    },
    // Inputs inside metadata container
    '& .input-field .tiptap.ProseMirror': {
      minHeight: 30,
      width: '100%',
      border: `1px solid ${theme.palette.grey[500]}`,
      borderRadius: '4px',
      fontSize: inputFontSizePx,
      margin: '.5em 0',
      wordBreak: 'break-word',
      padding: '5px 12px',
      transition: `box-shadow ${baseTransition}`,
      '&:focus-visible': {
        border: '1px solid transparent',
        outline: `1px solid ${theme.palette.primary.main}`,
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`, // Simulate border increase on focus
      },
    },
    '& .input-field': {
      '& .tiptap.ProseMirror *': {
        margin: 0,
      },
      '&.has-error .tiptap.ProseMirror': {
        borderColor: theme.palette.error.main,
        outline: `1px solid ${theme.palette.error.main}`,
        boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
      },
    },
    '& .error-notice': {
      color: theme.palette.error.main,
      '& svg': {
        fontSize: '16px',
      },
      '& .error-text': {
        fontSize: '15px',
      },
    },
    '& .has-error .ProseMirror': {
      border: `1px solid ${theme.palette.error.main} !important`,
      outline: `1px solid ${theme.palette.error.main}`,
      boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
    },
  };
};

export const inputWandStyle = {
  float: 'right',
  marginTop: 1,
};

export const inputWandWordCountContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  '.richtextinput-container': { width: '100%' },
  '.editorwithbar-container': { width: '100%' },
};

// Read only input
export const textInputStyle: CSSProperties = {
  '.MuiInputBase-input': {
    padding: '0 12px',
    fontSize: inputFontSizePx,
    height: inputHeightPx,
  },
  '& .Mui-readOnly': {
    opacity: 0.8,
    pointerEvents: 'none',
  },
};

export const getInlineInputContainerStyle = (fullWidth: boolean) => ({
  display: 'flex',
  flexDirection: fullWidth ? 'column' : 'row',
  gap: fullWidth ? 0 : '16px',
});

export const getTextInputStyleShort = (fullWidth: boolean) => ({
  ...textInputStyle,
  width: fullWidth ? '100%' : shortInputWidthPx,
});

// Input select
export const inputSelectStyle: CSSProperties = {
  minWidth: shortInputWidthPx,
  fontSize: inputFontSizePx,
  margin: inputMargin,
  '& .MuiSelect-select': {
    fontSize: inputFontSizePx,
    height: selectHeight,
    padding: '0px 12px 2px',
  },
  '& .MuiInputLabel-root': {
    fontSize: inputFontSizePx,
    padding: '0 12px',
    transform: 'translate(0)',
  },
  '& legend': {
    display: 'none',
  },
};

export const dateInputStyle: CSSProperties = {
  display: 'block',
  '& .MuiOutlinedInput-input': {
    padding: '0 12px',
    fontSize: inputFontSizePx,
    height: selectHeight,
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    display: 'none',
  },
};

export const getSelectInputStyle = (theme: Theme): SystemStyleObject => ({
  minWidth: shortInputWidthPx,
  fontSize: inputFontSizePx,
  margin: inputMargin,
  '& fieldset': {
    height: '30px',
  },
  '& .MuiSelect-select': {
    padding: '0px 12px',
  },
  '& legend': {
    display: 'none',
  },
  // Read only to match disabled styled
  '& .Mui-readOnly': {
    pointerEvents: 'none',
    pointer: 'default',
  },
  '&.Mui-readOnly .MuiSvgIcon-root': {
    color:
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[400],
  },
  '&.Mui-readOnly.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
  },
});

export const getImageUploadStyle = (fullWidth: boolean): SystemStyleObject => ({
  width: fullWidth ? '100%' : shortInputWidthPx,
  margin: inputMargin,
  '& img': {
    width: '100%',
  },
});

export const nonArticleContentBundleSx = (theme: Theme): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  fontSize: theme.typography.fontSize,
  gap: 2,
  p: 1,
  '& > *': {
    alignSelf: 'center', // Center all first level children
    width: '100%',
    maxWidth: formContentMaxWidthPx,
  },
  '& > *:last-child': { mb: marginOverscroll }, // overscroll for ease of use
});

export const nonArticleRichTextInputSx = (
  theme: Theme,
  options?: { type?: string },
): SystemStyleObject => {
  const isTextArea = options?.type === 'textarea';

  return {
    fontSize: theme.typography.fontSize,
    '& .ProseMirror': {
      textWrap: 'pretty !important',
      textRendering: 'optimizeLegibility !important',
      fontFeatureSettings: 'normal !important',
      borderBottom: '1px solid transparent',

      '& p.is-empty:first-of-type::before': {
        content: 'attr(data-placeholder)',
        color: theme.palette.grey[500],
        pointerEvents: 'none',
      },
      '&.ProseMirror-focused p.is-empty:first-of-type::before': {
        display: 'none',
      },

      '&:focus, :hover': {
        outline: 'none',
      },
    },

    '& .input-field.input-text .tiptap.ProseMirror, & .input-field.input-text .tiptap.ProseMirror p':
      {
        my: '0.5rem',
      },

    // Error notices under tiptap inputs
    '& .error-notice': {
      color: theme.palette.error.main,
      '& svg': {
        fontSize: theme.typography.fontSize,
      },
      '& .error-text': {
        fontSize: theme.typography.fontSize,
      },
    },

    '& .has-error > .ProseMirror': {
      borderBottom: `2px solid ${theme.palette.error.main} !important`,
    },

    '& .input-field .tiptap.ProseMirror': {
      px: 1,
      borderRadius: radiusHalf(theme),
      backgroundColor: theme.palette.action.hover,
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: theme.palette.action.hover,
      ...(isTextArea && {
        minHeight: textAreaInputHeight,
      }),
      '&:hover': {
        outline: `2px solid ${theme.palette.action.hover}`,
        outlineOffset: -2,
        backgroundColor: theme.palette.action.hover,
      },
      '&:focus-within': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -2,
        backgroundColor: theme.palette.action.hover,
      },
    },
  };
};

export const textAreaInputSx = (theme: Theme): SystemStyleObject => ({
  '& .input-field .tiptap.ProseMirror': {
    minHeight: `${textAreaInputHeight}px !important`,
  },
});

export const codeRichTextInputSx = (
  theme: Theme,
  editorIsDarkMode: boolean,
): SystemStyleObject => ({
  p: 0,
  mx: 0,
  mb: 0,
  mt: 1,
  minHeight: '200px !important',

  '& .error-notice': {
    color: theme.palette.error.main,
    '& svg': {
      fontSize: theme.typography.fontSize,
    },
    '& .error-text': {
      fontSize: theme.typography.fontSize,
    },
  },

  '& .has-error > .ProseMirror': {
    borderBottom: `2px solid ${theme.palette.error.main} !important`,
  },

  '& .input-field .tiptap.ProseMirror': {
    p: 0,
    m: 0,
    minHeight: '200px !important', // these min-heights fix CMSADMIN-416
    borderRadius: '10px',
    borderStyle: 'solid',
    borderWidth: '2px',
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.action.hover,
    '&:focus': {
      minHeight: '200px !important',
    },
    '&:hover': {
      minHeight: '200px !important',
      outline: `2px solid ${theme.palette.action.hover}`,
      outlineOffset: -2,
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus-within': {
      minHeight: '200px !important',
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
      backgroundColor: theme.palette.action.hover,
    },
    // Styles for the NodeViewWrapper and NodeViewContent
    '& .codeblock-wrapper, & .codeblock-content': {
      margin: '0px',
      padding: '8px',
      fontFamily: 'monospace',
      minHeight: '200px !important',
      borderRadius: '8px',
      fontSize: '13px',
      whiteSpace: 'normal',
      background: editorIsDarkMode ? '#282828' : '#f5f5f5',
      color: editorIsDarkMode ? '#ebdbb2' : '#24292e',
      '& code': {
        color: 'inherit',
        fontSize: 'inherit',
        background: 'inherit',
        margin: '0px',
        padding: '0px',
      },
      // Syntax highlighting styles
      '& .hljs-comment, & .hljs-quote, & .hljs-deletion': {
        color: editorIsDarkMode ? '#9c8e86' : '#238446',
        fontStyle: 'italic',
      },
      '& .hljs-variable, & .hljs-template-variable, & .hljs-template-tag, & .hljs-attribute, & .hljs-tag, & .hljs-name, & .hljs-regexp, & .hljs-link, & .hljs-selector-id, & .hljs-selector-class ':
        {
          color: editorIsDarkMode ? '#94cc85' : '#0549d2',
          fontWeight: 'bold',
        },
      '& .hljs-number, & .hljs-meta, & .hljs-built_in, & .hljs-builtin-name & .hljs-literal, & .hljs-doctag, & .hljs-type, & .hljs-link, & .hljs-params ':
        {
          color: editorIsDarkMode ? '#82ced1' : '#e36209',
        },
      '& .hljs-string, & .hljs-undefined, & .hljs-symbol, & .hljs-bullet': {
        color: editorIsDarkMode ? '#e1a021' : '#a61010',
      },
      '& .hljs-title, & .hljs-section, & .hljs-keyword, & .hljs-addition, & .hljs-function, & .hljs-selector-tag':
        {
          color: editorIsDarkMode ? '#d3869b' : '#1c8970',
          fontWeight: 'bold',
        },
      '& .hljs-emphasis': {
        fontStyle: 'italic',
      },
      '& .hljs-strong': {
        fontWeight: '700',
      },
    },
  },
});

// Bundle icon style
export const bundleItemIconStyle: SystemStyleObject = { minWidth: '35px' };
